import React, { useState, useEffect, useRef } from 'react';
import {
  Square, CheckSquare, CircleDot, Plus, X, Calendar, UserPlus,
} from 'lucide-react';
import { differenceInCalendarDays } from 'date-fns';
import UserAvatar from './UserAvatar';
import './SubtaskList.css';

/*
 * Rich subtasks (WR-011).
 *
 * A subtask carries its own status, assignee and due date, and is still an
 * embedded child of its task: it never reaches the board, has no children,
 * and is saved as part of its parent.
 *
 * ── The one interaction ───────────────────────────────────────────────
 *
 * The status box on the left cycles To do → In progress → Done → To do.
 * One control, one gesture, three states — rather than a checkbox that can
 * only express two of them sitting next to a dropdown that can express all
 * three. The tooltip names the next state so the cycle is discoverable.
 */

const STATUS_ORDER = ['todo', 'inprogress', 'done'];

const STATUS_META = {
  todo:       { label: 'To do',       icon: Square },
  inprogress: { label: 'In progress', icon: CircleDot },
  done:       { label: 'Done',        icon: CheckSquare },
};

/**
 * A subtask's status, tolerating the pre-WR-011 `{ text, completed }` shape.
 *
 * The backend normalises on save and a migration script backfills in bulk,
 * but a task that has not been saved since the upgrade still arrives with no
 * `status`. This is the client-side half of the same mapping, and the reason
 * an unmigrated task renders correctly instead of showing finished work as
 * To do. Exported — TaskCard counts progress with it.
 */
export function subtaskStatus(subtask) {
  if (STATUS_ORDER.includes(subtask?.status)) return subtask.status;
  return subtask?.completed ? 'done' : 'todo';
}

const nextStatus = (status) =>
  STATUS_ORDER[(STATUS_ORDER.indexOf(status) + 1) % STATUS_ORDER.length];

/** Relative deadline label plus the tone it should be drawn in. */
function dueMeta(value) {
  if (!value) return null;
  const days = differenceInCalendarDays(new Date(value), new Date());
  if (days < 0) {
    const over = -days;
    return { label: `${over}d overdue`, tone: 'overdue' };
  }
  if (days === 0) return { label: 'today',    tone: 'urgent' };
  if (days === 1) return { label: 'tomorrow', tone: 'urgent' };
  return { label: `in ${days}d`, tone: days <= 7 ? 'soon' : 'normal' };
}

/** <input type="date"> wants YYYY-MM-DD; the model stores a Date. */
const toDateInput = (value) => (value ? new Date(value).toISOString().split('T')[0] : '');

function SubtaskRow({ subtask, index, members, onPatch, onDelete }) {
  const [showAssignee, setShowAssignee] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const rowRef = useRef(null);
  // The title as it was when the field took focus, so a click through a row
  // that changes nothing doesn't cost a save.
  const titleOnFocus = useRef(subtask.text);

  useEffect(() => {
    if (!showAssignee) return;
    const close = (e) => {
      if (rowRef.current && !rowRef.current.contains(e.target)) setShowAssignee(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showAssignee]);

  const status = subtaskStatus(subtask);
  const meta = STATUS_META[status];
  const StatusIcon = meta.icon;
  const due = dueMeta(subtask.dueDate);
  const assignee = subtask.assignee;

  // Completing a subtask is a status change on an embedded item; it
  // deliberately does not fire the WR-009 task celebration, which belongs to
  // finishing the task itself.
  const cycleStatus = () => onPatch(index, { status: nextStatus(status) });

  return (
    <div className={`subtask-row subtask-${status}`} ref={rowRef}>
      <button
        type="button"
        className={`subtask-status subtask-status-${status}`}
        onClick={cycleStatus}
        title={`${meta.label} — click for ${STATUS_META[nextStatus(status)].label}`}
        aria-label={`Subtask status: ${meta.label}`}
      >
        <StatusIcon size={16} />
      </button>

      {/* Title edits stay local while you type and persist on blur — one
          save per edit, not one per keystroke. Every other control on the
          row is a discrete choice and saves immediately. */}
      <input
        type="text"
        className="subtask-text"
        value={subtask.text}
        onChange={e => onPatch(index, { text: e.target.value }, false)}
        onFocus={() => { titleOnFocus.current = subtask.text; }}
        onBlur={() => {
          if (subtask.text !== titleOnFocus.current) onPatch(index, {}, true);
        }}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        aria-label="Subtask title"
      />

      <div className="subtask-controls">
        {/* Due date — a pill once set, a bare icon until then. */}
        {showDate ? (
          <input
            type="date"
            className="subtask-date-input"
            value={toDateInput(subtask.dueDate)}
            autoFocus
            onBlur={() => setShowDate(false)}
            onChange={e => onPatch(index, { dueDate: e.target.value || null })}
          />
        ) : (
          <button
            type="button"
            className={`subtask-due ${due ? `subtask-due-${due.tone}` : 'subtask-due-empty'}`}
            onClick={() => setShowDate(true)}
            title={due ? `Due ${new Date(subtask.dueDate).toLocaleDateString()}` : 'Set a due date'}
          >
            <Calendar size={13} />
            {due && <span>{due.label}</span>}
          </button>
        )}

        {/* Assignee — restricted to project members, which is what the
            server enforces too. */}
        <div className="subtask-assignee-wrap">
          <button
            type="button"
            className="subtask-assignee"
            onClick={() => setShowAssignee(o => !o)}
            title={assignee ? `Assigned to ${assignee.name}` : 'Assign a member'}
          >
            {assignee
              ? <UserAvatar user={assignee} className="avatar" style={{ width: '22px', height: '22px', fontSize: '9px' }} />
              : <UserPlus size={14} />}
          </button>

          {showAssignee && (
            <div className="subtask-assignee-menu">
              {assignee && (
                <button
                  type="button"
                  className="subtask-assignee-item is-clear"
                  onClick={() => { onPatch(index, { assignee: null }); setShowAssignee(false); }}
                >
                  Unassign
                </button>
              )}
              {members.map(member => (
                <button
                  key={member._id}
                  type="button"
                  className="subtask-assignee-item"
                  onClick={() => { onPatch(index, { assignee: member }); setShowAssignee(false); }}
                >
                  <UserAvatar user={member} className="avatar" style={{ width: '22px', height: '22px', fontSize: '9px' }} />
                  <span>{member.name}</span>
                  {assignee?._id === member._id && <span className="subtask-assignee-check">✓</span>}
                </button>
              ))}
              {members.length === 0 && (
                <div className="subtask-assignee-empty">No project members.</div>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className="subtask-delete"
          onClick={() => onDelete(index)}
          title="Delete subtask"
          aria-label="Delete subtask"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * Props:
 *   subtasks — the task's subtask array
 *   members  — project members (and owner); the only valid assignees
 *   onChange — (nextSubtasks, persist) => void. The parent owns persistence;
 *              `persist` false means "hold this in the editor for now",
 *              which is what keeps typing a title off the wire.
 */
function SubtaskList({ subtasks = [], members = [], onChange }) {
  const [newText, setNewText] = useState('');

  const doneCount = subtasks.filter(st => subtaskStatus(st) === 'done').length;

  /**
   * Patch one row. `status` is written explicitly and `completed` alongside
   * it, so the payload the server receives is already self-consistent — the
   * model's pre-save hook then has nothing to correct.
   */
  const patch = (index, changes, persist = true) => {
    onChange(subtasks.map((st, i) => {
      if (i !== index) return st;
      const merged = { ...st, ...changes };
      if (changes.status !== undefined) merged.completed = changes.status === 'done';
      return merged;
    }), persist);
  };

  const remove = (index) => onChange(subtasks.filter((_, i) => i !== index), true);

  // Adding still needs only a title; status defaults to To do and the other
  // two fields are set afterwards on the row.
  const add = () => {
    const text = newText.trim();
    if (!text) return;
    onChange([...subtasks, { text, status: 'todo', completed: false, assignee: null, dueDate: null }], true);
    setNewText('');
  };

  return (
    <div className="task-subtasks-section">
      <label className="subtask-section-label">
        <CheckSquare size={16} />
        Subtasks
        {subtasks.length > 0 && (
          <span className="subtask-count">({doneCount}/{subtasks.length})</span>
        )}
      </label>

      {subtasks.length > 0 && (
        <div className="subtasks-list">
          {subtasks.map((subtask, index) => (
            <SubtaskRow
              key={subtask._id || `new-${index}`}
              subtask={subtask}
              index={index}
              members={members}
              onPatch={patch}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      <div className="subtask-input">
        <input
          type="text"
          placeholder="Add a subtask..."
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button className="btn btn-secondary btn-sm" onClick={add} disabled={!newText.trim()}>
          <Plus size={16} />
          Add
        </button>
      </div>
    </div>
  );
}

export default SubtaskList;
